import { useTranslation } from 'react-i18next';
import { Badge } from '@/shared/components/ui/badge';
import { useInstantFormat } from '@/shared/hooks/useInstantFormat';
import type { MyOverdueTaskDto } from '../types';

interface MyOverdueTasksTableRowProps {
  task: MyOverdueTaskDto;
}

const PRIORITY_STYLES: Record<string, string> = {
  None: 'bg-muted text-muted-foreground',
  Low: 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-400',
  Medium: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-400',
  High: 'bg-purple-50 text-purple-700 dark:bg-purple-950 dark:text-purple-400',
  Urgent: 'bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-400',
};

export function MyOverdueTasksTableRow({ task }: MyOverdueTasksTableRowProps) {
  const { t } = useTranslation('personalSummary');
  const formatInstant = useInstantFormat();

  const priorityStyle = PRIORITY_STYLES[task.priority ?? 'None'] ?? PRIORITY_STYLES.None;
  const priorityLabel = t(`priority.${task.priority ?? 'None'}`);
  const statusLabel = task.statusName ?? t('statusType.Todo');
  const formattedDueDate = formatInstant(task.dueDate);
  const statusBadgeStyle = task.statusColor
    ? { backgroundColor: `${task.statusColor}1a`, color: task.statusColor }
    : undefined;

  return (
    <tr
      className="border-b last:border-0 hover:bg-accent/30"
      data-testid={`personal-summary-overdue-row-${task.id}`}
    >
      <td className="max-w-[200px] py-3 pl-4 pr-2">
        <span className="line-clamp-1 text-sm font-medium">{task.title ?? '—'}</span>
      </td>

      <td className="max-w-[160px] px-2 py-3">
        <div className="flex items-center gap-1.5">
          <span
            className="h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: task.projectColor ?? '#6b7280' }}
          />
          <span className="min-w-0 truncate text-sm text-muted-foreground">
            {task.projectName ?? '—'}
          </span>
        </div>
      </td>

      <td className="px-2 py-3 text-sm font-medium text-destructive">{formattedDueDate}</td>

      <td className="px-2 py-3">
        <Badge className="bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-400">
          +{task.delayDays}d
        </Badge>
      </td>

      <td className="px-2 py-3">
        <Badge className={priorityStyle}>{priorityLabel}</Badge>
      </td>

      <td className="px-2 py-3">
        <Badge variant="secondary" style={statusBadgeStyle}>
          {statusLabel}
        </Badge>
      </td>

      <td className="max-w-[220px] px-2 py-3 pr-4">
        <span
          className="line-clamp-1 text-sm text-muted-foreground"
          title={task.blockedReason ?? ''}
        >
          {task.blockedReason ?? t('overdue.motivoEmpty')}
        </span>
      </td>
    </tr>
  );
}
